/**
 * QMP-FE-8c — STEP 1 of v83: «Quality Work Packages & Parties», a VIEW
 * (docs/design/mockups/mockup-qmp-v83-official.html `packagePage`
 * l. 1809–1825; dark values of the `#cqa-qmp-flow` tokens).
 *
 * The table only READS the plan (`GET /qmp` `structure[]`): one row per live
 * package — Executing · Verification · Acceptance · Action —, «Not assigned»
 * (the owner's one word, 04/11) as an amber badge, «None» muted, and in red
 * «Parties changed after the rules were saved · n rules to review» when the
 * server serves `needs_review` for the package (not while a revision is in
 * force: v83 `state.published ? 0`). Nothing here saves: «Edit» opens the
 * full-screen editor «<QWP> · parties» (`PackagePartiesPage`), «+ Add a Quality
 * Work Package» opens it empty. Read-only (a revision in force, or a caller who
 * does not govern — the condition 8d/8e1 use): «View», no «+ Add».
 *
 * QMP-FE-1 (v79) drew a table that saved on change (three doors, one per
 * position); it left with this train (D44).
 */
import React from 'react';

// v83 tokens, dark values (as `RuleListPage`).
const H2 = 'text-[20px] leading-[1.3] mb-[7px] font-bold';
const H3 = 'text-[15px] mb-[5px] font-bold';
const INTRO = 'text-[#afbdd0] max-w-[72ch]';
const DEFINITION = 'text-[#afbdd0] text-[13px] mt-[8px] mb-[16px] [&_strong]:font-bold [&_strong]:text-[#f1f5fb]';
const INFO = 'border-l-[3px] border-[#ff8b32] px-[13px] py-[10px] bg-[#32251e] my-[14px] text-[#f1f5fb] [&_strong]:font-bold';
const PANEL = 'bg-[#101a2b] border border-[#2b3c56] rounded-[10px] p-[18px] my-[16px] max-[580px]:p-[14px]';
const PANEL_HEAD = 'flex items-start justify-between gap-[12px] flex-wrap mb-[12px]';
const SMALL = 'text-[12px] text-[#afbdd0]';
const TH = 'text-left text-[#afbdd0] text-[11px] font-bold px-[8px] py-[10px] border-b border-[#2b3c56] '
  + 'first:pl-0 last:pr-0';
const TD = 'px-[8px] py-[11px] align-middle border-b border-[#2b3c56] first:pl-0 last:pr-0 '
  + 'max-[580px]:block max-[580px]:w-full max-[580px]:px-0 max-[580px]:py-[4px] max-[580px]:border-0';
const TABLE_BUTTON = 'min-h-[40px] border border-[#2b3c56] rounded-[7px] p-[8px] text-[12px] text-[#f1f5fb] bg-[#0b1423] '
  + 'max-[580px]:min-h-[44px]';
const QUIET = 'min-h-[44px] border border-transparent rounded-[7px] px-[4px] py-[9px] bg-transparent text-[#ff8b32] '
  + 'text-left';
const TBA_BADGE = 'inline-block px-[9px] py-[4px] rounded-[5px] text-[12px] font-bold text-[#ffd28b] bg-[#332819]';
const SOURCE = 'block text-[11px] mt-[3px] font-normal';

export const NOT_ASSIGNED_LABEL = 'Not assigned';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const names = (list) => {
  if (list.length < 2) return list.join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
};

/** How many rules of a package the server asks to review (one per section). */
export function rulesToReview(packages, qwpId) {
  const items = (packages && packages[qwpId] && packages[qwpId].needs_review) || [];
  return new Set(items.filter((n) => n && n.section).map((n) => n.section)).size;
}

function PartyCell({ party, executing, testId }) {
  if (executing && party && party.appointed === false) {
    return <span className={TBA_BADGE} data-testid={testId}>{NOT_ASSIGNED_LABEL}</span>;
  }
  if (!party || !party.participation_id) {
    return <span className={SMALL} data-testid={testId}>None</span>;
  }
  return <span data-testid={testId}>{party.name || ''}</span>;
}

export default function PackagesPartiesStep({
  qmp, readOnly, inForce, onEdit, onAdd,
}) {
  const { error: loadError } = qmp;
  const rows = qmp.structure || [];
  const tba = rows.filter((r) => r.executing && r.executing.appointed === false);

  return (
    <div data-testid="qs-step1">
      <h2 className={H2}>1. Quality Work Packages &amp; Parties</h2>
      <p className={INTRO} data-testid="qs-step1-intro">
        Set up the Quality Work Packages and name the companies that execute, verify and accept the work in each one.
      </p>
      <div className={DEFINITION} data-testid="qs-definition">
        <strong>Quality Work Package (QWP)</strong>
        {' is a scope of work, such as Civil works or Piling.'}
      </div>
      <div className={INFO} data-testid="qs-active-note">
        <strong>Companies and packages are active as soon as they are saved.</strong>
        {' The QMP rules stay in draft until they are published.'}
      </div>

      {loadError && !rows.length && (
        <div className="mt-[10px] mb-[10px] text-[12.5px] text-[#ffa9b2]" data-testid="qs-load-error">
          {loadError}
        </div>
      )}

      <section className={PANEL} data-testid="qs-packages-panel">
        <div className={PANEL_HEAD}>
          <div>
            <h3 className={H3} data-testid="qs-packages-count">
              {plural(rows.length, 'Quality Work Package', 'Quality Work Packages')}
            </h3>
            <p className={SMALL}>
              Executing Party: does the work · Verification Party: checks it · Acceptance Party: formally accepts
              it, where that role exists.
            </p>
          </div>
        </div>
        <table className="w-full border-collapse table-fixed text-[13px] max-[580px]:block" data-testid="qs-struct">
          <thead className="max-[580px]:hidden">
            <tr>
              <th className={`${TH} w-[26%]`}>Quality Work Package</th>
              <th className={TH}>Executing Party</th>
              <th className={TH}>Verification Party</th>
              <th className={TH}>Acceptance Party</th>
              <th className={`${TH} w-[76px]`}>Action</th>
            </tr>
          </thead>
          <tbody className="max-[580px]:block">
            {rows.map((row) => {
              const id = row.qwp_id;
              const flagged = inForce ? 0 : rulesToReview(qmp.packages, id);
              return (
                <tr
                  key={id}
                  className="max-[580px]:block max-[580px]:py-[12px] max-[580px]:border-b max-[580px]:border-[#2b3c56]"
                  data-testid={`qs-row-${id}`}
                >
                  <td className={`${TD} font-bold`} data-testid={`qs-name-${id}`}>
                    {row.name}
                    {flagged > 0 && (
                      <span className={`${SOURCE} text-[#ffa9b2]`} data-testid={`qs-flag-${id}`}>
                        {`Parties changed after the rules were saved · ${plural(flagged, 'rule', 'rules')} to review`}
                      </span>
                    )}
                  </td>
                  <td className={TD} data-label="Executing Party">
                    <PartyCell party={row.executing} executing testId={`qs-ep-${id}`} />
                  </td>
                  <td className={TD} data-label="Verification Party">
                    <PartyCell party={row.verification} testId={`qs-vp-${id}`} />
                  </td>
                  <td className={TD} data-label="Acceptance Party">
                    <PartyCell party={row.acceptance} testId={`qs-ap-${id}`} />
                  </td>
                  <td className={TD}>
                    <button
                      type="button"
                      className={TABLE_BUTTON}
                      onClick={() => onEdit(id)}
                      data-testid={`qs-edit-${id}`}
                    >
                      {readOnly ? 'View' : 'Edit'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!readOnly && (
          <div className="mt-[10px]">
            <button type="button" className={QUIET} onClick={onAdd} data-testid="qs-add">
              + Add a Quality Work Package
            </button>
          </div>
        )}
      </section>
      {tba.length > 0 && (
        <div className={INFO} data-testid="qs-tba-note">
          <strong>{NOT_ASSIGNED_LABEL}</strong>
          {` — ${names(tba.map((r) => r.name))} can be published before its Executing Party is named. When the company `
            + 'is appointed, it is asked to approve the rules of that package.'}
        </div>
      )}
    </div>
  );
}
